import styled, { css } from 'styled-components';
import { MilestoneStatusCode } from '@bechtel/types';
import { Box } from '@bechtel/gds';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import blockedPattern from '@bechtel/client-assets/dist/images/pattern-red.svg';
import {
  MilestoneStylingProps,
  MilestoneStatusExtended,
  MilestoneCardStyledProps,
  MilestoneBodyStyledProps,
} from './types';

function getCardStyles({ status }: MilestoneStylingProps) {
  switch (status) {
    case MilestoneStatusCode.PLANNED:
      return css`
        background-color: ${({ theme }) => theme.colors.blue[100]};
        border-color: ${({ theme }) => theme.colors.blue[300]};
      `;
    case MilestoneStatusCode.CLAIMED:
      return css`
        background-color: ${({ theme }) => theme.colors.orange[100]};
        border-color: ${({ theme }) => theme.colors.orange[300]};
      `;
    case MilestoneStatusCode.APPROVED:
      return css`
        background-color: ${({ theme }) => theme.colors.green[100]};
        border-color: ${({ theme }) => theme.colors.green[300]};
      `;
    case MilestoneStatusCode.REJECTED:
      return css`
        background-color: ${({ theme }) => theme.colors.purple[100]};
        border-color: ${({ theme }) => theme.colors.purple[300]};
      `;
    case MilestoneStatusExtended.BLOCKED:
      return css`
        background-color: ${({ theme }) => theme.colors.red[100]};
        border-color: ${({ theme }) => theme.colors.red[300]};
      `;
    case MilestoneStatusCode.UNPLANNED:
      return css`
        background-color: ${({ theme }) => theme.colors.grey[100]};
        border-color: ${({ theme }) => theme.colors.grey[300]};
      `;
    default:
      return css`
        background-color: ${({ theme }) => theme.colors.grey[100]};
        border-color: ${({ theme }) => theme.colors.grey[300]};
      `;
  }
}

function getIndicatorStyles({ status }: MilestoneStylingProps) {
  switch (status) {
    case MilestoneStatusCode.PLANNED:
      return css`
        background-color: ${({ theme }) => theme.colors.blue[500]};
      `;
    case MilestoneStatusCode.CLAIMED:
      return css`
        background-color: ${({ theme }) => theme.colors.orange[500]};
      `;
    case MilestoneStatusCode.APPROVED:
      return css`
        background-color: ${({ theme }) => theme.colors.green[500]};
      `;
    case MilestoneStatusCode.REJECTED:
      return css`
        background-color: ${({ theme }) => theme.colors.purple[500]};
      `;
    case MilestoneStatusCode.UNPLANNED:
      return css`
        background-color: ${({ theme }) => theme.colors.grey[500]};
      `;
    case MilestoneStatusExtended.BLOCKED:
      return css`
        background-color: ${({ theme }) => theme.colors.red[400]};
        background-image: url(${blockedPattern});
        background-repeat: repeat;
        background-size: ${({ theme }) => theme.fontSizes[500]};
      `;
    default:
      return css`
        background-color: ${({ theme }) => theme.colors.grey[500]};
      `;
  }
}

function getTitleStyles({ status }: MilestoneStylingProps) {
  switch (status) {
    case MilestoneStatusCode.PLANNED:
      return css`
        background-color: ${({ theme }) => theme.colors.blue[200]};
        border-bottom-color: ${({ theme }) => theme.colors.blue[300]};
      `;
    case MilestoneStatusCode.CLAIMED:
      return css`
        background-color: ${({ theme }) => theme.colors.orange[200]};
        border-bottom-color: ${({ theme }) => theme.colors.orange[300]};
      `;
    case MilestoneStatusCode.APPROVED:
      return css`
        background-color: ${({ theme }) => theme.colors.green[200]};
        border-bottom-color: ${({ theme }) => theme.colors.green[300]};
      `;
    case MilestoneStatusCode.REJECTED:
      return css`
        background-color: ${({ theme }) => theme.colors.purple[200]};
        border-bottom-color: ${({ theme }) => theme.colors.purple[300]};
      `;
    case MilestoneStatusExtended.BLOCKED:
      return css`
        background-color: ${({ theme }) => theme.colors.red[200]};
        border-bottom-color: ${({ theme }) => theme.colors.red[300]};
      `;
    case MilestoneStatusCode.UNPLANNED:
      return css`
        background-color: ${({ theme }) => theme.colors.grey[200]};
        border-bottom-color: ${({ theme }) => theme.colors.grey[300]};
      `;
    default:
      return css`
        background-color: ${({ theme }) => theme.colors.grey[200]};
        border-bottom-color: ${({ theme }) => theme.colors.grey[300]};
      `;
  }
}

interface MilestoneListStyledProps {
  milestonesCount: number;
}

export const CommodityMilestoneListStyled = styled.div<MilestoneListStyledProps>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
  /* 1600px is the minimum width required to accomodate 6 milestones without removing other entities in the UI */
  @media only screen and (min-width: 1600px) {
    ${({ milestonesCount }) =>
      milestonesCount > 5 &&
      css`
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
      `}
  }
  grid-auto-rows: 1fr;
  grid-gap: ${({ theme }) => theme.spacings[400]};
  padding: ${({ theme }) => theme.spacings[400]};
`;

export const MilestoneCardStyled = styled.div<MilestoneCardStyledProps>`
  border-radius: ${({ theme }) => theme.radii[200]};
  border-width: 1px;
  border-style: solid;
  display: flex;
  flex-direction: column;

  ${getCardStyles};
`;

export const MilestoneIndicatorStyled = styled.div`
  height: ${({ theme }) => theme.spacings[300]};
  ${getIndicatorStyles};
`;

export const MilestoneTitleStyled = styled.div`
  ${({ theme }) => css`
    border-bottom-width: 1px;
    border-bottom-style: solid;
    font-size: ${theme.fontSizes[400]};
    font-weight: ${theme.fontWeights[600]};
    padding: ${theme.spacings[300]};
    color: ${theme.colors.grey[1000]};
    font-family: ${theme.fontFamilies.graphik};
    text-align: center;
    position: relative;
    ${getTitleStyles};
  `}
`;

export const MilestoneBodyStyled = styled.div`
  flex-grow: 1;
  padding: ${({ theme }) => theme.spacings[400]};
  cursor: ${(props: MilestoneBodyStyledProps) => (props.isFullCardLinkRequired ? 'pointer' : 'default')};
`;

export const MilestoneAttachmentContainerStyled = styled(Box)`
  position: absolute;
  ${({ theme }) => css`
    top: ${theme.spacings[400]};
    right: ${theme.spacings[400]};
  `}
`;
